/**
 * Safari / iOS PWA resume recovery.
 *
 * When the tab or home-screen app sits in the background long enough, WebKit
 * often discards the JS heap. Returning paints a blank (or frozen) document
 * until modules reload. A bfcache restore can also leave a broken React tree.
 *
 * This module:
 * 1. Reloads once when pageshow reports a bfcache restore that looks empty.
 * 2. After a long background stretch, probes that React still owns #root;
 *    if not, triggers a one-shot reload (shares the chunk-reload cooldown).
 */

import { tryRecoverFromStaleChunk } from '@/lib/lazy-retry';

/** Away long enough that iOS likely discarded the page (≈ half a minute). */
export const LONG_BACKGROUND_MS = 30_000;

/** Marker set after the first successful React mount. */
export const APP_READY_ATTR = 'data-suturia-ready';

export function markAppReady(doc: Document = document): void {
  doc.documentElement.setAttribute(APP_READY_ATTR, '1');
}

export function isAppReady(doc: Document = document): boolean {
  return doc.documentElement.getAttribute(APP_READY_ATTR) === '1';
}

/**
 * True when #root has no React content — empty, or only the HTML boot splash
 * and/or the crawlable #seo-static block that ships in index.html for AI/SEO
 * readers (React replaces the whole root on mount).
 */
export function rootLooksBlank(root: Element | null): boolean {
  if (!root) return true;
  const kids = root.children;
  if (kids.length === 0) return true;
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i];
    if (!el) return false;
    const shell =
      el.hasAttribute('data-boot-splash') || el.hasAttribute('data-seo-static');
    if (!shell) return false;
  }
  return true;
}

type ResumeDeps = {
  document?: Document;
  window?: Window & typeof globalThis;
  now?: () => number;
  recover?: () => boolean;
  longBackgroundMs?: number;
};

/**
 * Install listeners. Safe to call once from boot(); returns an uninstall fn
 * for tests.
 */
export function installSafariResumeRecovery(deps: ResumeDeps = {}): () => void {
  const doc = deps.document ?? document;
  const win = deps.window ?? window;
  const now = deps.now ?? Date.now;
  const recover =
    deps.recover ??
    (() => tryRecoverFromStaleChunk());
  const longMs = deps.longBackgroundMs ?? LONG_BACKGROUND_MS;

  let hiddenAt = 0;

  const onVisibility = () => {
    if (doc.visibilityState === 'hidden') {
      hiddenAt = now();
      return;
    }
    if (doc.visibilityState !== 'visible') return;
    if (!hiddenAt) return;
    const away = now() - hiddenAt;
    hiddenAt = 0;
    if (away < longMs) return;
    // Long background: if the shell never remounted (or was wiped), reload.
    if (!isAppReady(doc) || rootLooksBlank(doc.getElementById('root'))) {
      recover();
    }
  };

  const onPageShow = (event: PageTransitionEvent) => {
    // bfcache restore: React trees frequently fail to rehydrate on iOS.
    // Prefer a clean reload over a stuck blank / dead UI.
    if (event.persisted) recover();
  };

  doc.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('pageshow', onPageShow as EventListener);

  return () => {
    doc.removeEventListener('visibilitychange', onVisibility);
    win.removeEventListener('pageshow', onPageShow as EventListener);
  };
}
